import { CAREERS_URL } from "../siteLinks";

/**
 * Logos are third-party marks shown as prior affiliations. `alt` names each
 * company so the row is meaningful without images.
 *
 * `aspect` is each mark's measured width/height after its view box was tightened
 * to the artwork. The row distributes width in proportion to it, which is what
 * makes the five together fill exactly one row.
 *
 * `scale` sets the box height, and `drop` sits every mark on ONE baseline.
 *
 * Centring the boxes does not align the letters, because the boxes contain
 * different things: Google's runs down to the descender of its "g", while
 * Meta's stops at the baseline. Measured from the artwork, the ink sits at:
 *
 *   Google    cap 3/202  baseline 154/202     Meta      cap 4/123  baseline 120/123
 *   Kumo      baseline 269/301                Stanford  baseline 167/188
 *   Snorkel   baseline 138/176
 *
 * So Google's letters landed about 3px higher than Meta's at a matched size.
 * `drop` is the share of each box that hangs BELOW its baseline; the row is
 * bottom-aligned and each mark is pushed down by that share, which puts every
 * baseline on the same line and lets descenders and icons hang past it.
 *
 * With the baselines aligned, matching caps is just arithmetic: Google's "G" is
 * (154-3)/202 = 0.7475 of its box and Meta's "M" is (120-4)/123 = 0.9431 of
 * its own, so Meta's box needs 0.7475 / 0.9431 = 0.793 to render the two at an
 * identical height.
 */
const PRIOR_WORK = [
  { name: "Google", file: "google.svg", aspect: 3.04, scale: 1, drop: 0.238 },
  { name: "Meta", file: "meta.png", aspect: 4.888, scale: 0.793, drop: 0.024 },
  /*
   * Kumo's "ku" is a solid magenta tile rather than letterforms. Shown in
   * colour at a matched height it out-weighed every other mark in the row, so
   * it is stepped down until it reads at the same strength.
   */
  { name: "Kumo", file: "kumo.png", aspect: 1.993, scale: 0.85, drop: 0.106 },
  /*
   * Stanford is a two-line lockup, so matching its box to the others put its
   * shield 22.7px above the baseline where every other mark reached about 19.8
   * — it was the one logo breaking the top of the row. 19.8 / 22.7 = 0.87.
   */
  {
    name: "Stanford Health Care",
    file: "stanford-health-care.svg",
    aspect: 3.27,
    scale: 0.87,
    drop: 0.112,
  },
  { name: "Snorkel", file: "snorkel.svg", aspect: 3.5, scale: 1, drop: 0.216 },
] as const;

export function TeamSection() {
  return (
    <section className="team" aria-labelledby="team-title">
      {/*
       * The heading is the call to action — it carries the careers link itself,
       * which is why the separate "See available roles" row underneath is gone.
       */}
      <h2 className="team__title" id="team-title">
        <a className="team__title-link" href={CAREERS_URL} rel="noreferrer" target="_blank">
          Join us!
        </a>
      </h2>
      <p className="team__lede">
        Backbone is based in San&nbsp;Francisco. Our team has built and researched at:
      </p>
      <ul className="team__logos">
        {PRIOR_WORK.map((entry) => (
          <li
            key={entry.name}
            style={
              {
                "--logo-aspect": entry.aspect,
                "--logo-scale": entry.scale,
                "--logo-drop": entry.drop,
              } as React.CSSProperties
            }
          >
            <img alt={entry.name} src={`/logos/${entry.file}`} loading="lazy" decoding="async" />
          </li>
        ))}
      </ul>
      {/*
       * Last line before the footer. Non-breaking spaces inside the names so no
       * investor is split across a line break — "First Harmonic" was landing as
       * "First" / "Harmonic", which reads as two backers rather than one.
       */}
      <p className="team__lede team__investors">
        We are grateful for the support of our investors including Andreessen&nbsp;Horowitz,
        Lightspeed, Hanabi&nbsp;Capital, First&nbsp;Harmonic, Constellation and angels from OpenAI,
        Stanford, and more.
      </p>
    </section>
  );
}
